import React, { useEffect, useState } from 'react';

interface LoadingStep {
  text: string;
  duration: number; // Duration in milliseconds for this step
}

interface GenerationLoadingOverlayProps {
  isVisible: boolean;
  loadingSteps?: LoadingStep[];
  currentProgress?: number; // Manual progress override (0-100)
  onComplete?: () => void;
}

const DEFAULT_LOADING_STEPS: LoadingStep[] = [
  { text: '📤 Optimizing your image...', duration: 4000 },
  { text: '☁️ Uploading to cloud storage...', duration: 2000 },
  { text: '🤖 AI is working its magic...', duration: 6000 },
  { text: '🎨 Adding finishing touches...', duration: 2000 },
  { text: '✅ Almost ready...', duration: 6000 },
];

export const GenerationLoadingOverlay: React.FC<GenerationLoadingOverlayProps> = ({
  isVisible,
  loadingSteps = DEFAULT_LOADING_STEPS,
  currentProgress,
  onComplete,
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Reset when overlay becomes visible
  useEffect(() => {
    if (isVisible) {
      setCurrentStepIndex(0);
      setProgress(0);
      setIsAnimating(true);
    } else {
      setIsAnimating(false);
    }
  }, [isVisible]);

  // Handle automatic step progression and progress updates
  useEffect(() => {
    if (!isVisible || !isAnimating) return;

    // Use manual progress if provided
    if (currentProgress !== undefined) {
      setProgress(currentProgress);
      
      // Update step based on progress
      const stepProgress = currentProgress / 100;
      const stepIndex = Math.min(
        Math.floor(stepProgress * loadingSteps.length),
        loadingSteps.length - 1
      );
      setCurrentStepIndex(stepIndex);
      
      // Complete when progress reaches 100%
      if (currentProgress >= 100 && onComplete) {
        setTimeout(onComplete, 500);
      }
      return;
    }

    // Auto-progression mode
    const totalDuration = loadingSteps.reduce((sum, step) => sum + step.duration, 0);
    let elapsedTime = 0;
    
    const interval = setInterval(() => {
      elapsedTime += 100; // Update every 100ms
      
      // Calculate current step and progress
      let stepStartTime = 0;
      let currentStep = 0;
      
      for (let i = 0; i < loadingSteps.length; i++) {
        if (elapsedTime >= stepStartTime && elapsedTime < stepStartTime + loadingSteps[i].duration) {
          currentStep = i;
          break;
        }
        stepStartTime += loadingSteps[i].duration;
        if (i === loadingSteps.length - 1) {
          currentStep = i;
        }
      }
      
      setCurrentStepIndex(currentStep);
      setProgress(Math.min((elapsedTime / totalDuration) * 100, 100));
      
      // Complete when all steps are done
      if (elapsedTime >= totalDuration) {
        clearInterval(interval);
        if (onComplete) {
          setTimeout(onComplete, 500);
        }
      }
    }, 100);

    return () => clearInterval(interval);
  }, [isVisible, isAnimating, loadingSteps, currentProgress, onComplete]);

  if (!isVisible) return null;

  const currentStep = loadingSteps[currentStepIndex];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl p-8 mx-4 max-w-md w-full shadow-2xl">
        {/* Animated spinner */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            {/* Outer ring */}
            <div className="w-16 h-16 border-4 border-gray-200 rounded-full animate-spin">
              <div className="w-full h-full border-4 border-transparent border-t-blue-600 rounded-full"></div>
            </div>
            {/* Inner rotating dots */}
            <div className="absolute inset-2 flex items-center justify-center">
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>

        {/* Loading text with fade transition */}
        <div className="text-center mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Processing Your Image
          </h3>
          <p 
            key={currentStepIndex} 
            className="text-gray-600 animate-fade-in text-sm"
          >
            {currentStep?.text || 'Loading...'}
          </p>
        </div>

        {/* Progress bar */}
        <div className="w-full">
          <div className="flex justify-between text-xs text-gray-500 mb-2">
            <span>Progress</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-blue-500 to-purple-600 h-full rounded-full transition-all duration-300 ease-out relative"
              style={{ width: `${progress}%` }}
            >
              {/* Shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white via-transparent opacity-30 animate-shimmer"></div>
            </div>
          </div>
        </div>

        {/* Step indicators */}
        <div className="flex justify-center mt-4 space-x-2">
          {loadingSteps.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index <= currentStepIndex 
                  ? 'bg-blue-600 scale-110' 
                  : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
        
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
};

// Predefined loading sequences for different operations
export const LOADING_SEQUENCES = {
  AI_TOUCHUP: [
    { text: '📤 Optimizing your image...', duration: 2000 },
    { text: '☁️ Uploading to cloud storage...', duration: 2000 },
    { text: '🤖 AI is enhancing your image...', duration: 5000 },
    { text: '🎨 Applying improvements...', duration: 2000 },
    { text: '✅ Enhancement complete!', duration: 1000 },
  ] as LoadingStep[],
  
  AI_CLEANUP: [
    { text: '📤 Preparing your image...', duration: 2000 },
    { text: '☁️ Uploading for processing...', duration: 2000 },
    { text: '🤖 AI is cleaning your image...', duration: 5000 },
    { text: '🏠 Applying room improvements...', duration: 2000 },
    { text: '✅ Room transformation complete!', duration: 1000 },
  ] as LoadingStep[],
  
  MOODBOARD: [
    { text: '🎨 Analyzing your preferences...', duration: 2000 },
    { text: '🤖 AI is creating concepts...', duration: 4000 },
    { text: '🏠 Generating room designs...', duration: 4000 },
    { text: '✨ Adding final details...', duration: 2000 },
    { text: '✅ Moodboard ready!', duration: 1000 },
  ] as LoadingStep[],
  
  IMAGE_UPLOAD: [
    { text: '📤 Processing your image...', duration: 2000 },
    { text: '☁️ Uploading to gallery...', duration: 3000 },
    { text: '💾 Saving to your collection...', duration: 2000 },
    { text: '✅ Image saved successfully!', duration: 1000 },
  ] as LoadingStep[],
};